import React from "react";
import { Detail } from "@/components/common/DashboardBits";
import FileThumb from "@/components/visit/FileThumb";
import { LAND_RATE_SOURCES, LOCAL_BODY_TYPES, SECTION_LABELS, formatINR, formatSqft, labelOf, tagLabel, valuationBreakdown } from "@/lib/visitSchema";
import { CheckCircle2, Circle, Lock } from "lucide-react";

export const inr = (v) => (v != null && v !== "" ? `₹ ${Number(v).toLocaleString("en-IN")}` : "—");
export const dash = (v) => (v == null || v === "" ? "—" : v);

export function Section({ title, children, testId }) {
  return (
    <div className="mt-4" data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

export function Restricted({ children, testId }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid={testId}>
      <Lock className="h-3.5 w-3.5" /> {children}
    </div>
  );
}

const BASIC_FIELDS = [
  ["Owner", "owner_name"], ["Property type", "property_type"], ["Structure", "construction_type"], ["Floors", "floors"],
  ["North", "boundary_north"], ["South", "boundary_south"], ["East", "boundary_east"], ["West", "boundary_west"],
  ["Length (ft)", "plot_length_ft"], ["Width (ft)", "plot_width_ft"], ["Built-up (sqft)", "built_up_area_sqft"], ["Year", "year_of_construction"],
];

export function BasicDetails({ visit }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
      {BASIC_FIELDS.map(([label, key]) => <Detail key={key} label={label} value={dash(visit[key])} />)}
    </div>
  );
}

export function ValuationSection({ visit }) {
  const bd = valuationBreakdown(visit);
  const mode = visit.valuation_manual ? "Manual override" : bd.computed_valuation_inr != null ? "Auto-calculated" : "—";
  return (
    <Section title="Valuation" testId="summary-valuation-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Detail label="Plot area" value={formatSqft(bd.plot_area_sqft)} />
        <Detail label="Land rate (₹/sq ft)" value={visit.plot_land_rate_per_sqft != null ? inr(visit.plot_land_rate_per_sqft) : "—"} />
        <Detail label="Plot value" value={formatINR(bd.plot_value_inr)} />
        <Detail label="Construction cost (₹/sq ft)" value={visit.construction_cost_per_sqft != null ? inr(visit.construction_cost_per_sqft) : "—"} />
        <Detail label="Construction value" value={formatINR(bd.construction_value_inr)} />
        <Detail label="Mode" value={mode} />
      </div>
      {visit.valuation_amount_inr != null && (
        <div className="mt-3 num text-lg text-emerald-700 font-semibold" data-testid="summary-valuation">
          {inr(visit.valuation_amount_inr)}
          {visit.valuation_manual && bd.computed_valuation_inr != null && <span className="ml-2 text-xs font-normal text-slate-500">(calculated {formatINR(bd.computed_valuation_inr)})</span>}
        </div>
      )}
    </Section>
  );
}

export function VerificationSections({ visit, khasraCount }) {
  const nb = visit.neighbor || {};
  const lr = visit.land_rate || {};
  return (
    <>
      <Section title="Neighbor & local body verification" testId="summary-neighbor">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Detail label="Neighbor" value={dash(nb.name)} /><Detail label="Phone" value={dash(nb.phone)} />
          <Detail label="Address" value={dash(nb.address)} /><Detail label="Local body" value={labelOf(LOCAL_BODY_TYPES, nb.local_body_type)} />
        </div>
      </Section>
      <Section title="Land rate verification" testId="summary-land-rate">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Detail label="Source" value={labelOf(LAND_RATE_SOURCES, lr.source_type)} /><Detail label="Name" value={dash(lr.source_name)} /><Detail label="Phone" value={dash(lr.source_phone)} />
          <Detail label="Khasra documents" value={khasraCount} />
        </div>
      </Section>
    </>
  );
}

export function LoanLeadsTable({ leads }) {
  if (!leads.length) return <div className="text-sm text-slate-500">No loan leads recorded.</div>;
  return (
    <table className="w-full text-sm" data-testid="summary-loan-leads">
      <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Customer</th><th>Phone</th><th>Loan amount</th><th>Telecalling</th></tr></thead>
      <tbody>
        {leads.map((l, i) => (
          <tr key={`${l.phone || ""}-${l.customer_name || ""}-${i}`} className="border-t border-slate-100">
            <td className="py-1.5">{dash(l.customer_name)}</td><td>{dash(l.phone)}</td><td className="num">{inr(l.loan_amount_inr)}</td>
            <td>{l.telecalling_verified ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Verified</span> : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><Circle className="h-3.5 w-3.5" /> Pending</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PhotoGroup({ title, items }) {
  if (!items.length) return null;
  return (
    <div className="mb-3">
      <div className="text-xs text-slate-600 mb-1.5">{title} · {items.length}</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {items.map((p, i) => <FileThumb key={p.file_id || i} fileId={p.file_id} tag={p.tag} canPreview index={i} testId={`summary-photo-${p.file_id || i}`} />)}
      </div>
    </div>
  );
}

export function PhotoGroups({ byTag, verification }) {
  return (
    <>
      {Object.entries(byTag).map(([tag, items]) => <PhotoGroup key={tag} title={tagLabel(tag)} items={items} />)}
      {verification.map((g) => <PhotoGroup key={g.key} title={SECTION_LABELS[g.key]} items={g.items} />)}
    </>
  );
}
